import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { settingsService, AppSetting } from '../services/settingsService';

const PRICE_KEY = 'subscription.monthlyPrice';
const TRIAL_KEY = 'subscription.trialDays';

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [priceInput, setPriceInput] = useState('');
  const [trialInput, setTrialInput] = useState('');
  const [priceOriginal, setPriceOriginal] = useState('');
  const [trialOriginal, setTrialOriginal] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await settingsService.getAll();
      const price = data.find((s) => s.key === PRICE_KEY)?.value ?? '14.90';
      const trial = data.find((s) => s.key === TRIAL_KEY)?.value ?? '7';
      const priceBr = price.replace('.', ',');
      setPriceInput(priceBr);
      setPriceOriginal(priceBr);
      setTrialInput(trial);
      setTrialOriginal(trial);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          'Não foi possível carregar as configurações.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasChanges =
    priceInput !== priceOriginal || trialInput !== trialOriginal;

  const handleSave = async () => {
    setError(null);
    // Validação
    const priceNormalized = priceInput.replace(',', '.').trim();
    const priceNumber = parseFloat(priceNormalized);
    if (isNaN(priceNumber) || priceNumber <= 0) {
      setError('Preço inválido. Use um valor maior que zero (ex: 14,90).');
      return;
    }
    const trialNumber = parseInt(trialInput, 10);
    if (isNaN(trialNumber) || trialNumber < 0 || trialNumber > 365) {
      setError('Dias de trial inválido. Use um número entre 0 e 365.');
      return;
    }

    try {
      setSaving(true);
      const updates: Array<Promise<AppSetting>> = [];
      if (priceInput !== priceOriginal) {
        updates.push(settingsService.update(PRICE_KEY, priceNumber.toFixed(2)));
      }
      if (trialInput !== trialOriginal) {
        updates.push(settingsService.update(TRIAL_KEY, String(trialNumber)));
      }
      await Promise.all(updates);
      setPriceOriginal(priceInput);
      setTrialOriginal(trialInput);
      setSuccess(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          'Não foi possível salvar as configurações.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ mb: 4, fontWeight: 600 }}
      >
        Configurações
      </Typography>

      <Stack spacing={3}>
        {/* Assinatura */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Assinatura Premium
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Estes valores são usados no app mobile, no site e no momento da
              cobrança recorrente no Asaas.
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Stack spacing={2} sx={{ maxWidth: 420 }}>
                  <TextField
                    label="Preço mensal"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }}
                    helperText="Ex: 14,90"
                    fullWidth
                  />

                  <TextField
                    label="Dias de trial gratuito"
                    type="number"
                    value={trialInput}
                    onChange={(e) => setTrialInput(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0, max: 365, step: 1 }}
                    helperText="Defina 0 para desabilitar o trial"
                    fullWidth
                  />
                </Stack>

                <Divider sx={{ my: 3 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box display="flex" gap={2} alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                  >
                    {saving ? 'Salvando...' : 'Salvar alterações'}
                  </Button>
                  {hasChanges && !saving && (
                    <Typography variant="body2" color="warning.main">
                      Alterações não salvas
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={success}
        autoHideDuration={3500}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" variant="filled">
          Configurações salvas. O novo valor é aplicado imediatamente.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
